import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useEffectiveRole } from '@/features/impersonation/useImpersonation';
import type { UserRole } from '@shared/types';
import { InsufficientRole } from './InsufficientRole';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles: readonly UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const effectiveRole = useEffectiveRole();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-fw-text-muted text-sm">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Gate on the *effective* role so impersonation genuinely swaps the UI —
  // an admin previewing as a manager shouldn't be able to navigate into
  // admin-only pages.
  //
  // Sprint M cycle 3 (2026-05-15) — F-CYCLE3-P3. Was a silent
  // `<Navigate to="/" />` which bounced e.g. a manager hitting
  // /leadership/analytics to /manager with no feedback (looked broken).
  // Now renders an explicit access-restricted surface. The unauthenticated
  // case above still redirects to /login (correct — no identity yet).
  if (!effectiveRole || !allowedRoles.includes(effectiveRole)) {
    return <InsufficientRole allowedRoles={allowedRoles} />;
  }

  return <>{children}</>;
}
